import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { RouteParamsService } from '../../services/route-params.service';
import { Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import {
  ClientSelected,
  ClientSelectionState
} from '../../state/ClientSelectionState/ClientSelection.state';
import { InstanceTypes } from '../../models/instance-types';
import { UserManagementService } from '../../services/user-management.service';
import { AuthService } from '../../services/auth.service';
import {
  EmployerSelectionState,
  SetPersonId,
  SetSelectedEmployer
} from '../../state/EmployerSelectionState/EmployerSelection.state';

@Component({
  selector: 'lib-client-nav-wrapper',
  templateUrl: './client-nav-wrapper.component.html',
  styleUrls: ['./client-nav-wrapper.component.css'],
  standalone: false
})
export class ClientNavWrapperComponent implements OnInit, OnDestroy {
  public activeTab: string;
  private dataSub: Subscription;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private routeParamsService: RouteParamsService,
    private userManagementService: UserManagementService,
    private authService: AuthService,
    private store: Store,
    @Inject('env') private env: any
  ) {}

  ngOnInit(): void {
    //Components such as the Nav Header or the Release notes that aren't
    //Directly linked to a route are unable to access route params normally.
    //So we are going to load the params observable into a service
    //So that we can access them elsewhere.
    this.routeParamsService.setParams(this.route.paramMap);
    const codeInState = this.store.selectSnapshot(ClientSelectionState.clientCode);
    const params = this.route.snapshot.paramMap;
    const codeInRoute = params.get('client_code');
    if (codeInState == null) {
      //state doesn't have a client code. By this point, it should.
      //This likely means someone had a bookmark or a link somewhere.
      this.store.dispatch(new ClientSelected(codeInRoute));
    } else if (codeInState != codeInRoute) {
      //This means someone manually navigated. Update state.
      this.store.dispatch(new ClientSelected(codeInRoute));
    }
    if (this.env.instanceType === InstanceTypes.EmployeePortal) {
      const userId = this.authService.user.UserId;
      if (userId != null && userId != '') {
        this.userManagementService.getUserEmployers(userId).subscribe(employers => {
          if (employers == null || employers.length == 0) {
            this.router.navigate(['/access-denied']);
          } else if (this.route.routeConfig != null && this.route.routeConfig.path == 'settings') {
            this.router.navigate([`/${employers[0].ClientCode}/settings`]);
          } else if (employers.length == 1) {
            //only one employer. Set the values, if they aren't already.
            const employerInState = this.store.selectSnapshot(
              EmployerSelectionState.selectedEmployer
            );
            if (employerInState == null || employerInState.ClientCode != employers[0].ClientCode) {
              this.store.dispatch(new SetSelectedEmployer(employers[0]));
              this.store.dispatch(new SetPersonId(employers[0].PersonId));
            }
          } else {
            //multiple employers. Send them to the client select page if they aren't already set.
            const employerInState = this.store.selectSnapshot(
              EmployerSelectionState.selectedEmployer
            );
            if (employerInState == null) {
              this.router.navigate(['/clientselect']);
            }
          }
        });
      }
    }

    this.routeParamsService.setQueryParams(this.route.queryParamMap);
    this.dataSub = this.route.data.subscribe(data => {
      this.activeTab = data.activeTab || 'Home';
    });
    // Use the initial route passed into this nav component to get the starting route data.
    // Angular passes us the base route which contains the ClientNavWrapperComponent since that is where this
    // component is being rendered from.  However, we want the bottom most child route to get the
    // correct route data, so traverse to that element before we parse the data.
    this.getActiveRoute(this.route).data.subscribe(data => {
      this.setActiveTab(data);
    });

    // This component is not reconstructed on each navigation event,
    // so we must subscribe to the router events to get updated data
    // from our routes.
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.getActiveRoute(this.route).data.subscribe(data => {
          this.setActiveTab(data);
        });
      }
    });
  }

  setActiveTab(data: Data) {
    this.activeTab = data.activeTab || 'Home';
  }

  getActiveRoute(route: ActivatedRoute): ActivatedRoute {
    if (route.firstChild == undefined) {
      return route;
    }

    return this.getActiveRoute(route.firstChild);
  }
  ngOnDestroy(): void {
    this.dataSub.unsubscribe();
  }
}
